import Service from '@ember/service';
import { service } from '@ember/service';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import PrettyNum from '../utils/pretty-num-wrapper';
import Consts from '../utils/constants';
import { htmlSafe } from '@ember/template';
import Utils from '../utils/utils';

export default class ErrorHandlingService extends Service {
    @service('data') model;
    @service selectionData;
    @service intl;
    @service tabService;
    @service circuitConfigurations;
    @service driverSupplyService;
    @service sigmaDeltaData;
    @service plotDataService;

    //#region adc input errors

    @computed(
        'model.{samplingRate,minSamplingRate}',
        'selectionData.selectedAdc.{maxSampleRate,disableSampleRateValidation}',
    )
    get samplingRateError() {
        let model = this.model;
        let intl = this.intl;
        let selectedAdc = this.selectionData.selectedAdc;

        if (!selectedAdc.disableSampleRateValidation && model.samplingRate > selectedAdc.maxSampleRate) {
            return intl.t('messages.max', {
                field: intl.t('input_labels.sampling_rate'),
                value: PrettyNum.eng3(selectedAdc.maxSampleRate),
            });
        } else if (!selectedAdc.disableSampleRateValidation && model.samplingRate < model.minSamplingRate) {
            return intl.t('messages.min_adc_sampling_rate', { minRate: PrettyNum.eng3(model.minSamplingRate) });
        } else {
            return '';
        }
    }

    @computed('model.vref', 'selectionData.selectedAdc.{vrefMax,vrefMin}') get vrefError() {
        let model = this.model;
        let intl = this.intl;
        let selectedAdc = this.selectionData.selectedAdc;

        if (Utils.isNumeric(selectedAdc.vrefMax) && model.vref > selectedAdc.vrefMax) {
            return intl.t('messages.max', { field: intl.t('input_labels.vref'), value: selectedAdc.vrefMax });
        } else if (Utils.isNumeric(selectedAdc.vrefMin) && model.vref < selectedAdc.vrefMin) {
            return intl.t('messages.min', { field: intl.t('input_labels.vref'), value: selectedAdc.vrefMin });
        } else {
            return '';
        }
    }

    @computed('samplingRateError', 'vrefError') get adcInputsError() {
        return this.vrefError || this.samplingRateError ? true : false;
    }

    //#endregion adc input errors

    //#region driver input errors

    @computed('selectionData.{selectedDriver,selectedTopology.id}', 'model.{vref,rext,cext,frequency,rf,gain,rg}')
    get driverOutputError() {
        let vref = this.model.vref,
            rext = this.model.rext,
            cext = this.model.cext,
            frequency = this.model.frequency,
            selectedTopology = this.selectionData.selectedTopology.id,
            rf = this.model.rf,
            gain = this.model.gain,
            rg = this.model.rg,
            data = {
                iOutLimit: this.selectionData.selectedDriver.IoutLimit,
                iOutRc: vref / 2 / (rext + 1 / (2 * Math.PI * frequency * cext)),
                iOutFeedback:
                    selectedTopology === Consts.TOPOLOGY.INVERTING
                        ? vref / (2 * rf)
                        : selectedTopology === Consts.TOPOLOGY.NON_INVERTING && gain != 1
                          ? vref / (2 * (rf + rg))
                          : 0,
            };

        let rules = [
            function (data) {
                return {
                    bodyArgs: { value: PrettyNum.eng3(data.iOutRc) },
                    inValid: data.iOutRc > data.iOutLimit,
                    translation: 'messages.driver_output.filt_check_error',
                };
            },
            function (data) {
                return {
                    bodyArgs: { value: PrettyNum.eng3(data.iOutFeedback) },
                    inValid: data.iOutFeedback > data.iOutLimit,
                    translation: 'messages.driver_output.rf_check_error',
                };
            },
        ];

        return this.model.validateRules(rules, data);
    }

    @computed('model.{vout,vref}') get voutError() {
        let model = this.model;
        let intl = this.intl;

        if (model.vout > model.vref) {
            return intl.t('messages.max', { field: intl.t('input_labels.vout'), value: PrettyNum.eng3(model.vref) });
        } else if (model.vout < model.minVout) {
            return intl.t('messages.min', {
                field: intl.t('input_labels.vout'),
                value: PrettyNum.eng3(this.model.minVout),
            });
        } else return '';
    }

    @computed('model.gain') get gainError() {
        let gain = this.model.gain,
            intl = this.intl;

        if (this.baseGainError) {
            return this.baseGainError;
        } else if (gain > this.model.maxWarningGain) {
            return intl.t('messages.gain_max');
        } else {
            return this.baseGainError;
        }
    }

    @computed('model.gain') get gainPanelError() {
        return this.baseGainError;
    }

    @computed('model.gain') get baseGainError() {
        let gain = this.model.gain,
            intl = this.intl;

        if (gain > this.model.maxGain) {
            return intl.t('messages.max', {
                field: intl.t('input_labels.gain'),
                value: this.model.maxGain,
            });
        } else if (gain < this.model.minGain) {
            return intl.t('messages.min', {
                field: intl.t('input_labels.gain'),
                value: this.model.minGain,
            });
        } else return '';
    }

    @computed(
        'selectionData.{selectedDriver,selectedTopology.id,selectedSourceType,selectedAdc.type}',
        'model.{rf,rfDisabled,rg}',
    )
    get rfError() {
        let intl = this.intl,
            rf = this.model.rf,
            rg = this.model.rg,
            rfRgRatio = rf / rg,
            rfLowerLimit = this.selectionData.selectedDriver.rfLowerLimit,
            selectedTopology = this.selectionData.selectedTopology.id,
            driverType = this.selectionData.selectedDriver.type,
            unityGain = this.selectionData.selectedDriver.unityGain,
            sourceType = this.selectionData.selectedSourceType.id,
            adcType = this.selectionData.selectedAdc.type,
            cin = this.selectionData.selectedDriver.Cin,
            cdiff = this.selectionData.selectedDriver.Cdiff,
            rfDisabled = this.model.rfDisabled,
            // max RF to ensure Fpole-Cin is at least Fcr * 4
            maxRfStageOne =
                (Math.pow(rfRgRatio, 2) + 2 * rfRgRatio + 1) / (2 * Math.PI * 4 * unityGain * (cin + cdiff)),
            maxRfStageTwo = (Math.pow(1, 2) + 2 * 1 + 1) / (2 * Math.PI * 4 * unityGain * (cin + cdiff)),
            maxRf = 0,
            secondStageExists = false,
            errorMessage = '';

        if (
            !(
                driverType === Consts.DRIVER_TYPE.DIFF_AMP ||
                adcType === Consts.ADC_TYPE.SE ||
                sourceType === Consts.SOURCE_TYPE.DIFF
            )
        ) {
            secondStageExists = true;
        }

        //If rf = 0, then there is only a first stage, and it is a follower
        if (rf === 0 && !secondStageExists) {
            maxRf = maxRfStageOne;
            errorMessage = intl.t('messages.rf_max', {
                gain: this.model.gain,
                rfMax: PrettyNum.eng(maxRf, 2),
            });
        } else if (driverType === Consts.DRIVER_TYPE.DIFF_AMP && !secondStageExists) {
            maxRf = maxRfStageOne;
            errorMessage = intl.t('messages.rf_max', {
                gain: this.model.gain,
                rfMax: PrettyNum.eng(maxRf, 2),
            });
            //if rf !=0 and rg = inf, then first stage is follower, but there is a second stage
        } else if (rg === Infinity && secondStageExists) {
            maxRf = maxRfStageTwo;
            errorMessage = intl.t('messages.rf_secondstage_max', {
                gain: 2,
                rfMax: PrettyNum.eng(maxRf, 2),
            });
        } else if (!secondStageExists) {
            maxRf = maxRfStageOne;
            errorMessage = intl.t('messages.rf_max', {
                gain: this.model.gain,
                rfMax: PrettyNum.eng(maxRf, 2),
            });
        } else {
            maxRf = Math.min(maxRfStageOne, maxRfStageTwo);
            if (maxRf == maxRfStageOne) {
                errorMessage = intl.t('messages.rf_max', {
                    gain: this.model.gain,
                    rfMax: PrettyNum.eng(maxRf, 2),
                });
            } else {
                errorMessage = intl.t('messages.rf_secondstage_max', {
                    gain: 2,
                    rfMax: PrettyNum.eng(maxRf, 2),
                });
            }
        }

        // set to 2 significant digits, and convert back to numbers
        maxRf = PrettyNum.userEntryToNumber(PrettyNum.eng(maxRf, 2));

        console.log('Rf check: Rf = ' + rf + ', Rg = ' + rg);
        console.log('Rf check: MaxRfStageOne = ' + maxRfStageOne + ', MaxRfStageTwo = ' + maxRfStageTwo);
        console.log('Rf check: maxRf = ' + maxRf + ', rfRgRatio = ' + rfRgRatio);

        if (rf > maxRf) {
            return errorMessage;
        }
        // Only show lower limit warning when RF is not disabled.
        else if (rf < rfLowerLimit && !rfDisabled) {
            // Can't we just use intl and the pretty-num helper on these errors?
            return intl.t('messages.rf_min', {
                minimum: PrettyNum.eng3(rfLowerLimit),
                driver: this.selectionData.selectedDriver.partCore,
            });
        } else if (rf === 0 && selectedTopology === Consts.TOPOLOGY.INVERTING) {
            return intl.t('messages.rf');
        } else return '';
    }

    @computed('vSupplyError', 'driverOutputError') get driverError() {
        return this.vSupplyError || this.driverOutputError;
    }

    @computed('voutError', 'rfError', 'gainPanelError') get driverInputsError() {
        return this.voutError || this.rfError || this.gainPanelError ? true : false;
    }

    //#endregion driver input errors

    //#region filter input errors

    // @computed('model.{rext,filterEnabled}', 'selectionData.{selectedDriver,selectedAdc}')
    // get rextError() {
    //     let model = this.model;
    //     let intl = this.intl;
    //     let selectedDriver = this.selectionData.selectedDriver;
    //     let selectedAdc = this.selectionData.selectedAdc;
    //     // Use default minRext value if part doesn't have an rfiltMin
    //     let driverMinRext = selectedDriver.rfiltMin !== undefined ? selectedDriver.rfiltMin : model.minRext;
    //     let adcMinRext = selectedAdc.rfiltMin !== undefined ? selectedAdc.rfiltMin : model.minRext;
    //     let minRext = Math.max(driverMinRext, adcMinRext);
    //     let maxRext = model.maxRext;

    //     // Check if the selected ADC is AD4080 and has rfiltMin and rfiltMax
    //     if (selectedAdc.partCore === 'AD4080' && selectedAdc.rfiltMin !== undefined && selectedAdc.rfiltMax !== undefined) {
    //         minRext = selectedAdc.rfiltMin;
    //         maxRext = selectedAdc.rfiltMax;

    //         if (model.rext < minRext || model.rext > maxRext) {
    //             return intl.t('messages.ad4080_rext_error', {
    //                 adc: selectedAdc.partCore,
    //                 rfiltMin: PrettyNum.eng3(minRext),
    //                 rfiltMax: PrettyNum.eng3(maxRext)
    //             });
    //         }
    //     }

    //     if (model.filterEnabled && model.rext > maxRext) {
    //         return intl.t('messages.max', { field: intl.t('input_labels.rext'), value: PrettyNum.eng3(maxRext) });
    //     } else if (model.filterEnabled && model.rext < minRext) {
    //         if (minRext === driverMinRext) {
    //             return intl.t('messages.rext_filter_min', {
    //                 driver: selectedDriver.partCore,
    //                 rextMin: PrettyNum.eng3(driverMinRext),
    //             });
    //         } else if (minRext === adcMinRext) {
    //             return intl.t('messages.rext_filter_min_adc', {
    //                 adc: selectedAdc.partCore,
    //                 rextMin: PrettyNum.eng3(adcMinRext),
    //             });
    //         }
    //     } else return '';
    // }

    // @computed('model.{rext,filterEnabled}', 'selectionData.{selectedDriver,selectedAdc}')
    // get rextError() {
    //     let model = this.model;
    //     let intl = this.intl;
    //     let selectedDriver = this.selectionData.selectedDriver;
    //     // Use default minRext value if part doesn't have an rfiltMin
    //     let minRext = typeof selectedDriver.rfiltMin == 'undefined' ? model.minRext : selectedDriver.rfiltMin;

    //     if (model.filterEnabled && model.rext > model.maxRext) {
    //         return intl.t('messages.max', { field: intl.t('input_labels.rext'), value: PrettyNum.eng3(model.maxRext) });
    //     } else if (model.filterEnabled && model.rext < minRext) {
    //         return intl.t('messages.rext_filter_min', {
    //             driver: selectedDriver.partCore,
    //             rextMin: selectedDriver.rfiltMin,
    //         });
    //     } else return '';
    // }
    @computed('model.{rext,filterEnabled}', 'selectionData.{selectedDriver,selectedAdc}')
    get rextError() {
        let model = this.model;
        let intl = this.intl;
        let selectedDriver = this.selectionData.selectedDriver;
        // Use default minRext value if part doesn't have an rfiltMin
        let selectedAdc = this.selectionData.selectedAdc;
        let minRext = typeof selectedDriver.rfiltMin == 'undefined' ? model.minRext : selectedDriver.rfiltMin;
        let maxRext = model.maxRext;

        // Check if the selected ADC is AD4080, AD4081, or AD4084 and has rfiltMin and rfiltMax
        if (
            model.filterEnabled &&
            ['AD4080', 'AD4081', 'AD4084'].includes(selectedAdc.partCore) &&
            selectedAdc.rfiltMin !== undefined &&
            selectedAdc.rfiltMax !== undefined
        ) {
            minRext = selectedAdc.rfiltMin;
            maxRext = selectedAdc.rfiltMax;

            if (model.rext < minRext || model.rext > maxRext) {
                return intl.t('messages.ad4080_rext_error', {
                    adc: selectedAdc.partCore,
                    rfiltMin: PrettyNum.eng3(minRext),
                    rfiltMax: PrettyNum.eng3(maxRext),
                });
            }
        }

        if (model.filterEnabled && model.rext > model.maxRext) {
            return intl.t('messages.max', { field: intl.t('input_labels.rext'), value: PrettyNum.eng3(model.maxRext) });
        } else if (model.filterEnabled && model.rext < minRext) {
            return intl.t('messages.rext_filter_min', {
                driver: selectedDriver.partCore,
                rextMin: selectedDriver.rfiltMin,
            });
        } else return '';
    }

    @computed('model.{cext,filterEnabled}', 'selectionData.{selectedDriver,selectedAdc}')
    get cextError() {
        let model = this.model;
        let intl = this.intl;

        if (model.filterEnabled && model.cext < model.minCext) {
            return intl.t('messages.min', { field: intl.t('input_labels.cext'), value: PrettyNum.eng3(model.minCext) });
        } else if (model.filterEnabled && model.cext > model.maxCext) {
            return intl.t('messages.max', { field: intl.t('input_labels.cext'), value: PrettyNum.eng3(model.maxCext) });
        } else return '';
    }

    @computed('cextError', 'rextError') get filterInputsError() {
        return this.cextError || this.rextError ? true : false;
    }

    //#endregion filter input errors

    //#region signal input errors

    @computed('model.{frequency,samplingRate,inputSlewRate}', 'selectionData.selectedDriver')
    get frequencyError() {
        let model = this.model;
        let inputSlewRate = this.model.inputSlewRate;
        let driverSlewRate = this.selectionData.selectedDriver.slewRate;
        let maxValidSamplingRate = 0.5 * model.samplingRate;
        let intl = this.intl;

        if (model.frequency < model.minFrequency) {
            return intl.t('messages.min', {
                field: intl.t('input_labels.frequency'),
                value: PrettyNum.eng3(model.minFrequency),
            });
        } else if (model.frequency > maxValidSamplingRate) {
            return intl.t('messages.max_input_frequency', {
                sample: PrettyNum.eng3(model.samplingRate),
                max: PrettyNum.eng3(maxValidSamplingRate),
            });
        } else if (inputSlewRate > driverSlewRate) {
            return intl.t('messages.input_frequency_slew', {
                driverSR: PrettyNum.eng3(driverSlewRate / 1e6),
                inputSR: PrettyNum.eng3(inputSlewRate / 1e6),
                driver: this.selectionData.selectedDriver.partCore,
            });
        } else return '';
    }

    @computed('model.{vcm,vcmLabelText}', 'model.vcmRange.{min,max}') get vcmError() {
        if (
            this.model.vcmRange &&
            (this.model.vcm < this.model.vcmRange.min || this.model.vcm > this.model.vcmRange.max)
        ) {
            return this.intl.t('messages.vcm_range', {
                min: this.model.vcmRange.min,
                max: this.model.vcmRange.max,
                label: this.model.vcmLabelText,
            });
        } else return '';
    }

    @computed('circuitConfigurations.currentCircuitConfig.inputSourceTypeError')
    get sourceTypeError() {
        if (this.circuitConfigurations.currentCircuitConfig.inputSourceTypeError) {
            return this.intl.t('messages.input_source_type');
        } else return '';
    }

    @computed('frequencyError', 'sourceTypeError') get inputInputsError() {
        return this.frequencyError || this.sourceTypeError ? true : false;
    }

    //#endregion signal input errors

    //#region noise panel errors

    @computed('model.wbNoise') get wbNoiseError() {
        let model = this.model;
        let intl = this.intl;

        if (model.wbNoise > model.maxWbNoise) {
            return intl.t('messages.max', {
                field: intl.t('input_labels.wb_noise'),
                value: PrettyNum.eng3(model.maxWbNoise),
            });
        } else if (model.wbNoise < model.minWbNoise) {
            return intl.t('messages.min', {
                field: intl.t('input_labels.wb_noise'),
                value: PrettyNum.eng3(model.minWbNoise),
            });
        } else {
            return '';
        }
    }

    @computed('model.thd') get thdError() {
        let model = this.model;
        let intl = this.intl;

        if (model.thd > model.maxThd) {
            return intl.t('messages.max', { field: intl.t('input_labels.thd'), value: model.maxThd });
        } else if (model.thd < model.minThd) {
            return intl.t('messages.min', { field: intl.t('input_labels.thd'), value: model.minThd });
        } else {
            return '';
        }
    }

    @computed('model.{noiseCalculations,filterBandwidth}') get systemNoiseWarn() {
        let noiseCalculations = this.model.noiseCalculations,
            driverTotalNoise = noiseCalculations.driver.totalVrms,
            adcTotalNoise = noiseCalculations.adc.totalVrms,
            isWarn = driverTotalNoise > adcTotalNoise,
            intl = this.intl;

        if (isWarn) {
            return htmlSafe(
                intl.t('messages.system_driver_contrib', {
                    rcBandwidth: `${PrettyNum.eng3(this.model.filterBandwidth, { separatedUnit: true })}${intl.t(
                        'units.hz',
                    )}`,
                }),
            );
        }

        return '';
    }

    @computed('model.{adcThdEstimate,adcThdDatasheet}') get thdFreqWarn() {
        let intl = this.intl,
            adcThdPerfomanceDiff = this.model.adcThdEstimate - this.model.adcThdDatasheet,
            isWarn = adcThdPerfomanceDiff > 3;

        if (isWarn) {
            return intl.t('messages.thd_rfilt', {
                adc: this.selectionData.selectedAdc.partCore,
                freq: `${PrettyNum.eng3(this.model.frequency, { separatedUnit: true })}${intl.t('units.hz')}`,
                diff: `${Math.round(adcThdPerfomanceDiff)} ${intl.t('units.db')}`,
            });
        } else {
            return '';
        }
    }

    @computed('plotDataService.noiseArrays.{thdAmpAtF,thdAdcAtF}') get thdDriverWarn() {
        let isWarn = this.plotDataService.noiseArrays.thdAmpAtF > this.plotDataService.noiseArrays.thdAdcAtF;

        if (isWarn) {
            return this.intl.t('messages.thd_driver');
        } else {
            return '';
        }
    }

    @computed('thdFreqWarn', 'thdDriverWarn') get isThdOutputWarn() {
        return this.thdFreqWarn || this.thdDriverWarn ? true : false;
    }

    @computed('disabledTHDTabError', 'isThdOutputWarn', 'isSettlingNoiseError') get isThdTabError() {
        let disabledTHDTab = this.disabledTHDTabError;
        return disabledTHDTab ? false : this.isThdOutputWarn && !this.isSettlingNoiseError;
    }

    // This is for all errors inside the "Noise & Distortion tab, so that we can show the "!" circle.
    @computed('disabledTHDTabError', 'wbNoiseError', 'thdError', 'isThdOutputWarn', 'systemNoiseWarn')
    get isNoiseDistortionTabError() {
        let disabledTHDTab = this.disabledTHDTabError;
        return disabledTHDTab
            ? false
            : this.wbNoiseError || this.thdError || this.isThdOutputWarn || this.systemNoiseWarn
              ? true
              : false;
    }

    @computed('disabledTHDTabError', 'wbNoiseError', 'thdError') get isNoiseSimInputsError() {
        let disabledTHDTab = this.disabledTHDTabError;
        return disabledTHDTab ? false : this.wbNoiseError || this.thdError ? true : false;
    }

    //#endregion noise panel errors

    //#region settling panel errors

    //#region sigma delta errors

    @alias('sigmaDeltaData.odrError') odrError;

    @alias('sigmaDeltaData.mclkError') mclkError;

    @alias('sigmaDeltaData.fmodError') fmodError;

    //#endregion sigma delta errors

    @computed(
        'model.inputSettlingArrays.{requiredBwDc,requiredBwSin,requiredBwMuxed,minKickbackDominatesSin,lsbMaxDc,lsbMaxSin,lsbRingDc,lsbRingSin}',
        'model.{requiredBwMuxedTransition,filterBandwidth,multiplexed}',
        'tabService.tabs.items.settling.@each.{name,hide}',
        'selectionData.selectedAdc.id',
    )
    get bwWarnings() {
        let bwValues = [];
        let dcBw;
        let sinBw;
        let muxedBw;
        let muxedTBw;
        let message = '';
        let dcTabWarn = false;
        let sinTabWarn = false;
        let muxedTabWarn = false;
        let lsbErrorTrigger = 0.5;
        let ppmtolsbConvert = Math.pow(2, this.selectionData.selectedAdc.bits) / 1e6;

        let acTab = this.tabService.tabs.items.settling.find((tab) => tab.index === Consts.TAB_INDEX.SETTLING.AC);
        let hideAcTab = !acTab || acTab.hide;

        let inl_ppm = this.selectionData.selectedAdc.inl_ppm;
        if (inl_ppm !== undefined) {
            lsbErrorTrigger = inl_ppm * ppmtolsbConvert;
        }

        let inl_lsb = this.selectionData.selectedAdc.inl_lsb;
        if (inl_lsb !== undefined) {
            lsbErrorTrigger = inl_lsb;
        }

        // Check if warning and add the bw value to array

        console.log(
            '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~\nSettling Warning: Start BW Checks, ' +
                lsbErrorTrigger +
                ' LSBs triggers error',
        );
        if (
            this.model.inputSettlingArrays.requiredBwDc > this.model.filterBandwidth &&
            this.model.inputSettlingArrays.lsbMaxDc > lsbErrorTrigger
        ) {
            dcBw = this.model.inputSettlingArrays.requiredBwDc;
            bwValues.push(dcBw);
            dcTabWarn = true;
            console.log('   Settling Warning: DC due to RC BW limit, ' + dcBw);
        }
        if (
            !hideAcTab &&
            this.model.inputSettlingArrays.requiredBwSin > this.model.filterBandwidth &&
            this.model.inputSettlingArrays.lsbMaxSin > lsbErrorTrigger &&
            !this.model.inputSettlingArrays.minKickbackDominatesSin
        ) {
            sinBw = this.model.inputSettlingArrays.requiredBwSin;
            bwValues.push(sinBw);
            sinTabWarn = true;
            console.log('   Settling Warning: AC due to RC BW limit, ' + sinBw);
        }
        if (
            this.model.inputSettlingArrays.requiredBwMuxed > this.model.filterBandwidth &&
            this.model.inputSettlingArrays.lsbMaxMuxed > lsbErrorTrigger &&
            this.model.multiplexed
        ) {
            muxedBw = this.model.inputSettlingArrays.requiredBwMuxed;
            bwValues.push(muxedBw);
            muxedTabWarn = true;
            console.log('   Settling Warning: Mux Kickback due to RC BW limit, ' + muxedBw);
        }
        if (
            this.model.requiredBwMuxedTransition > this.model.filterBandwidth &&
            this.model.inputSettlingArrays.lsbMaxMuxed > lsbErrorTrigger &&
            this.model.multiplexed
        ) {
            muxedTBw = this.model.requiredBwMuxedTransition;
            bwValues.push(muxedTBw);
            muxedTabWarn = true;
            console.log('   Settling Warning: Mux Transition due to RC BW limit, ' + muxedBw);
        }

        if (bwValues.length > 0) {
            let max = Math.max.apply(null, bwValues);

            switch (max) {
                case dcBw: {
                    message = this.intl.t('messages.rc_bw_DC');
                    break;
                }
                case sinBw: {
                    message = this.intl.t('messages.rc_bw_AC');
                    break;
                }
                case muxedBw: {
                    message = this.intl.t('messages.rc_bw_MuxKick');
                    break;
                }
                case muxedTBw: {
                    message = this.intl.t('messages.rc_bw_MuxTransition');
                    break;
                }
                default:
                    break;
            }
        }

        let rcBwWarn = dcTabWarn || sinTabWarn || muxedTabWarn;
        let rcBwMessage = message;

        // SAR-198 & 200
        let triggerThdAdjust = true;
        console.log('Settling Warning: Starting ringing checks...');

        // For ADCs that have a Q-model in place, non settling will not turn off distortion plot, but ringing will
        let settlingTHDadj = this.selectionData.selectedAdc.settlingTHDadj;
        if (settlingTHDadj) {
            // If Muxed not settled
            if (this.model.inputSettlingArrays.lsbMaxMuxed > lsbErrorTrigger && this.model.multiplexed) {
                console.log('   Settling Warning: Muxed not settled');
                if (!message) message = this.intl.t('messages.not_settling_Muxed'); // Only show this message if there is not a previous error.

                muxedTabWarn = true;
                triggerThdAdjust = false;
            }

            // If AC not settled
            if (
                !hideAcTab &&
                !this.model.inputSettlingArrays.minKickbackDominatesSin &&
                this.model.inputSettlingArrays.lsbMaxSin > lsbErrorTrigger &&
                this.model.inputSettlingArrays.lsbRingSin
            ) {
                console.log('   Settling Warning: AC due to ringing');
                if (!message) message = this.intl.t('messages.not_settling_AC'); // Only show this message if there is not a previous error.

                sinTabWarn = true;
                triggerThdAdjust = false;
            }

            // If DC not settled
            if (this.model.inputSettlingArrays.lsbMaxDc > lsbErrorTrigger && this.model.inputSettlingArrays.lsbRingDc) {
                console.log('   Settling Warning: DC due to ringing');
                if (!message) message = this.intl.t('messages.not_settling_DC'); // Only show this message if there is not a previous error.

                dcTabWarn = true;
                triggerThdAdjust = false;
            }
        } else {
            // for ADCs without Q-model, any type of non settling will turn off distortion plot

            // If Muxed not settled
            if (this.model.inputSettlingArrays.lsbMaxMuxed > lsbErrorTrigger && this.model.multiplexed) {
                console.log('   Settling Warning: Muxed not settled');
                if (!message) message = this.intl.t('messages.not_settling_Muxed'); // Only show this message if there is not a previous error.

                muxedTabWarn = true;
            }

            // If AC not settled
            if (
                !hideAcTab &&
                !this.model.inputSettlingArrays.minKickbackDominatesSin &&
                this.model.inputSettlingArrays.lsbMaxSin > lsbErrorTrigger
            ) {
                console.log('   Settling Warning: AC due to ringing');
                if (!message) message = this.intl.t('messages.not_settling_AC'); // Only show this message if there is not a previous error.

                sinTabWarn = true;
            }

            // If DC not settled
            if (this.model.inputSettlingArrays.lsbMaxDc > lsbErrorTrigger) {
                console.log('   Settling Warning: DC due to ringing');
                if (!message) message = this.intl.t('messages.not_settling_DC'); // Only show this message if there is not a previous error.

                dcTabWarn = true;
            }
        }

        let settlingRingingWarn = rcBwWarn ? false : dcTabWarn || sinTabWarn || muxedTabWarn;
        let settlingRingingMessage = settlingRingingWarn ? message : '';

        return {
            rcBw: rcBwMessage,
            settlingRinging: settlingRingingMessage,

            isDcTab: dcTabWarn,
            isSinTab: sinTabWarn,
            isMuxedTab: muxedTabWarn,

            triggerThdAdjust: triggerThdAdjust,
        };
    }

    @computed('bwWarnings') get isSettlingTabError() {
        let bwWarnings = this.bwWarnings;
        return bwWarnings.settlingRinging || bwWarnings.rcBw ? true : false;
    }

    @computed('bwWarnings', 'isSettlingTabError', 'selectionData.selectedAdc.settlingTHDadj')
    get isSettlingNoiseError() {
        let settlingTHDadj = this.selectionData.selectedAdc.settlingTHDadj,
            isSettlingTabError = this.isSettlingTabError,
            bwWarnings = this.bwWarnings;

        // This is for blanking out the Noise subtabs.
        return settlingTHDadj ? (bwWarnings.settlingRinging ? true : false) : isSettlingTabError;
    }

    @computed('bwWarnings', 'model.thdSettlingAdjustment', 'selectionData.selectedAdc.thdBaseline')
    get thdAdjustmentWarning() {
        let thdSettlingAdjustment = this.model.thdSettlingAdjustment;
        let thdBaseline = -this.selectionData.selectedAdc.thdBaseline;
        let bwWarnings = this.bwWarnings;

        if (!thdSettlingAdjustment) {
            return '';
        } else if (bwWarnings.triggerThdAdjust && thdSettlingAdjustment >= thdBaseline) {
            return this.intl.t('messages.noise_thd'); // This is for blanking out the Noise subtabs.
        } else {
            return '';
        }
    }

    //#endregion settling panel errors

    //#region driver supply errors

    @alias('driverSupplyService.vSupplyError') vSupplyError;

    //#endregion driver supply errors

    @computed('selectionData.selectedAdc.partCore', 'sigmaDeltaData.filterType.{isSigmaDeltaAdc,disableTHDPlot}')
    get disabledTHDTabError() {
        if (
            this.sigmaDeltaData.isSigmaDeltaAdc &&
            this.sigmaDeltaData.filterType &&
            this.sigmaDeltaData.filterType.disableTHDPlot
        ) {
            return this.intl.t('messages.blanked_thd_tab_filter_type');
        }

        let selectedAdc = this.selectionData.selectedAdc;
        return selectedAdc.blankTHDTab ? this.intl.t('messages.blanked_thd_tab', { part: selectedAdc.partCore }) : '';
    }

    @computed('isSettlingNoiseError', 'vSupplyError', 'disabledTHDTabError') get disableNoiseAndDistortionSummary() {
        let settlingNoiseError = this.isSettlingNoiseError,
            vSupplyError = this.vSupplyError,
            disabledTHDTab = this.disabledTHDTabError;

        return !disabledTHDTab && (settlingNoiseError || vSupplyError);
    }

    @alias('circuitConfigurations.currentCircuitConfig.message') circuitError;

    @computed('adcInputsError', 'driverInputsError', 'filterInputsError', 'inputInputsError', 'circuitError')
    get tabError() {
        if (
            this.adcInputsError ||
            this.driverInputsError ||
            this.filterInputsError ||
            this.inputInputsError ||
            this.circuitError
        ) {
            return this.intl.t('messages.input_panel');
        } else return '';
    }

    @computed('isNoiseSimInputsError', 'isSettlingNoiseError', 'vSupplyError')
    get noiseSubtabError() {
        if (this.vSupplyError) {
            return this.intl.t('messages.vsupply');
        } else if (this.isNoiseSimInputsError) {
            return this.intl.t('messages.input_panel');
        } else if (this.isSettlingNoiseError) {
            return this.intl.t('messages.noise_settling');
        } else return '';
    }

    // Driver Performance Errors
    @computed('model.driverPerformanceData.tempABError') get tempABError() {
        return this.model.driverPerformanceData.tempABError;
    }

    @computed('model.driverPerformanceData.tempABErrorItems') get tempABErrorItems() {
        return this.model.driverPerformanceData.tempABErrorItems;
    }
}
